/* Nick's Aquarium: per-row product title alignment in the collection panels.
 *
 * Moved out of an inline <script> in sections/main-collection.liquid on 2026-08-23.
 *
 * 2026-08-23 (scroll lag): the alignment result is unchanged — every row's titles are still sized
 * to the tallest title in that row, so prices, stats and buttons line up exactly as before. What
 * changed is how often that measuring runs and how expensive each pass is. On a phone, scrolling
 * a long collection was pinning the main thread in here, which is what made the scroll feel
 * sticky and left images arriving late.
 *
 * Three causes, all addressed below:
 *   1. Every `resize` re-measured every card. A phone fires `resize` each time the URL bar slides
 *      in or out — i.e. constantly, while you scroll.
 *   2. Each row was measured and then written before the next row was measured, so the browser
 *      recalculated layout once PER ROW. On a 1,200-product grid that is hundreds of forced
 *      layouts in a single pass.
 *   3. A MutationObserver watched the entire panel subtree, so every attribute and node change
 *      anywhere inside it queued another pass.
 */
(() => {
  const equalizeGrid = (grid) => {
    const titles = grid.querySelectorAll("li.product-grid__item:not(.lv-hide) p[role='heading']");
    if (!titles.length) return;

    // Pass 1 — write only. Release every title to its natural height.
    titles.forEach((t) => {
      if (t.style.height !== 'auto') t.style.height = 'auto';
    });

    // Pass 2 — read only. Group by grid row (cards in a row share a top edge) and record the
    // tallest title in each. No writes in here, so the browser computes layout once for the whole
    // grid instead of once per row.
    const rows = new Map();
    titles.forEach((t) => {
      const li = t.closest('li.product-grid__item');
      if (!li) return;
      const top = Math.round(li.getBoundingClientRect().top);
      let row = rows.get(top);
      if (!row) {
        row = { titles: [], max: 0 };
        rows.set(top, row);
      }
      row.titles.push(t);
      const h = t.getBoundingClientRect().height;
      if (h > row.max) row.max = h;
    });

    // Pass 3 — write only. Rows with no wrapped title end up back at their natural height, and a
    // title already at the right height is left alone rather than rewritten.
    rows.forEach((row) => {
      const px = Math.ceil(row.max) + 'px';
      row.titles.forEach((t) => {
        if (t.style.height !== px) t.style.height = px;
      });
    });
  };

  let queued = false;
  const run = () => {
    if (queued) return;
    queued = true;
    // setTimeout (not requestAnimationFrame): rAF is paused while the tab is in the background,
    // which would leave this debounce stuck and never equalize until the tab is focused.
    // setTimeout still fires (throttled) in a hidden tab. 150ms coalesces the burst of mutations
    // a fetched page produces into a single pass.
    setTimeout(() => {
      queued = false;
      // Re-query every run: the in-stock grid replaces its children after a fetch, and
      // out-of-stock pages are appended, so cached node lists go stale.
      document.querySelectorAll('ul.fish-panel__grid').forEach(equalizeGrid);
    }, 150);
  };

  // Row grouping depends only on the WIDTH of the viewport. A height-only resize — which is all
  // a phone's URL bar sliding away produces — cannot change the answer, so re-measuring on it is
  // pure waste at the worst possible moment.
  let lastWidth = window.innerWidth;
  const onResize = () => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    run();
  };

  run();
  window.addEventListener('resize', onResize);
  window.addEventListener('load', run);
  // Belt-and-braces for the async in-stock fetch / image + webfont settle.
  [300, 1000, 2500].forEach((ms) => setTimeout(run, ms));
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);

  // The panels component swaps in fetched cards (replaceChildren) and appends lazy-loaded pages.
  // Watching the two grid lists for child changes catches both, and costs a fraction of watching
  // the whole panel subtree for everything.
  const grids = document.querySelectorAll('ul.fish-panel__grid');
  if (grids.length) {
    const observer = new MutationObserver(run);
    grids.forEach((grid) => observer.observe(grid, { childList: true }));
  } else {
    const host = document.querySelector('fish-collection-panels') || document.body;
    new MutationObserver(run).observe(host, { childList: true, subtree: true });
  }
})();
